import { createSlice } from "@reduxjs/toolkit";
import { sentenceToCamel, snakeToCamel } from "../../utils/transformString";

const initialState = {
  respiratory: {
    bloodOxygen: {
      id: "15",
      classId: 3,
      title: "Blood Oxygen",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "percentage",
      unit: {
        percentage: {
          name: "percentage",
          symbol: "%",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "%",
              name: "value",
              type: "number",
              step: "0.01",
              min: 50,
              max: 100,
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Blood Oxygen",
        },
        multiValue: false,
      },
    },

    respiratoryRate: {
      id: "16",
      classId: 3,
      title: "Respiratory Rate",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "breathsPerMinute",
      unit: {
        breathsPerMinute: {
          name: "breathsPerMinute",
          symbol: "BrPM",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "BrPM",
              name: "value",
              type: "number",
              step: "0.1",
              min: 1,
              max: 60,
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Breaths Per Minute",
        },
        multiValue: false,
      },
    },

    forcedVitalCapacity: {
      id: "respi3",
      classId: 3,
      title: "Forced Vital Capacity",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "liter",
      unit: {
        liter: {
          name: "liter",
          symbol: "L",
          to: (value) => parseFloat(value.toFixed(1)),
          from: (value) => parseFloat(value.value),
          formConfig: [
            {
              symbol: "L",
              name: "value",
              type: "number",
              step: "0.01",
              min: 0,
              max: 8,
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Force Vital Capicity",
        },
        multiValue: false,
      },
    },

    peakExpiratoryFlowRate: {
      id: "respi4",
      classId: 3,
      title: "Peak Expiratory Flow Rate",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "literPerMinute",
      unit: {
        literPerMinute: {
          name: "literPerMinute",
          symbol: "L/m",
          to: (value) => parseFloat(value?.toFixed(1)),
          from: (value) => parseFloat(value?.value),
          formConfig: [
            {
              symbol: "L/m",
              name: "value",
              type: "number",
              step: "0.01",
              min: 50,
              max: 1000,
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Peak Expiratory Flow Rate",
        },
        multiValue: false,
      },
    },

    forcedExpiratoryVolume: {
      id: "respi5",
      classId: 3,
      title: "Forced Expiratory Volume",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "liter",
      unit: {
        liter: {
          name: "liter",
          symbol: "L",
          to: (value) => parseFloat(value?.toFixed(1)),
          from: (value) => parseFloat(value?.value),
          formConfig: [
            {
              symbol: "L",
              name: "value",
              type: "number",
              step: "0.01",
              min: 0,
              max: 10,
            },
          ],
        },
      },
      chartConfig: {
        type: "bar",
        config: {
          label: "Forced Expiratory Volume, 1sec",
        },
        multiValue: false,
      },
    },

    sixMinuteWalk: {
      id: "respi6",
      classId: 3,
      title: "Six Minute Walk",
      type: "respiratory",
      data: [],
      favourite: false,
      selectedUnit: "meter",
      unit: {
        meter: {
          name: "meter",
          symbol: "m",
          to: (value) => parseFloat(value?.toFixed(1)),
          from: (value) => parseFloat(value?.value),
          formConfig: [
            {
              symbol: "m",
              name: "value",
              type: "number",
              step: "0.01",
              min: 0,
              max: 3000,
            },
          ],
        },
      },
      chartConfig: {
        type: "line",
        config: {
          label: "Six-Minute Walk",
        },
        multiValue: false,
      },
    },
  },
  componentState: {
    dataState: null,
    vitalsDataState: null,
    firstRun: true,
    firstClick: false,
  },
};

const respiratorySlice = createSlice({
  name: "respiratory",
  initialState,
  reducers: {
    addData(state, action) {
      const { responseItem, titleId } = action.payload;

      const key = Object.keys(state.respiratory).find(
        (key) => state.respiratory[key].id === titleId
      );

      state.respiratory[key].data.push({
        id: responseItem?.id,
        date: responseItem?.date,
        time: responseItem?.time,
        value: responseItem?.data.value,
      });
    },

    removeData(state, action) {
      const { id, titleId } = action.payload;
      const dataKey = Object.keys(state.respiratory).find(
        (key) => state.respiratory[key].id === titleId
      );

      state.respiratory[dataKey].data = state.respiratory[dataKey].data.filter(
        (item) => item.id !== id
      );
    },

    changeUnit(state, action) {
      const { unit, titleId } = action.payload;

      const dataKey = Object.keys(state.respiratory).find(
        (key) => state.respiratory[key].id === titleId
      );

      state.respiratory[dataKey].selectedUnit = unit;
    },

    toggleFavourite(state, action) {
      const { id } = action.payload;
      const dataKey = Object.keys(state.respiratory).find(
        (key) => state.respiratory[key].id === id
      );

      state.respiratory[dataKey].favourite =
        !state.respiratory[dataKey].favourite;
    },

    populateData(state, action) {
      const data = action.payload.data;
      const selectedUnit = action.payload.selectedUnit;
      const favourites = action.payload.favourites;
      const titles = action.payload.titles;

      for (let key in data) {
        const array = data[key].map((item) => {
          return {
            id: item.id,
            value: item.data.value,
            date: item.date,
            time: item.time,
          };
        });

        if (state.respiratory[key]) {
          state.respiratory[key].data = array;
        }
      }

      for (let key in selectedUnit) {
        const camelKey = snakeToCamel(key);

        if (state.respiratory[camelKey]) {
          state.respiratory[camelKey].selectedUnit = snakeToCamel(
            selectedUnit[key]
          );
        }
      }

      for (let key in favourites) {
        // const camelKey = snakeToCamel(key);
        if (state.respiratory[key]) {
          state.respiratory[key].favourite = favourites[key];
        }
      }

      titles.forEach((title) => {
        const camelName = sentenceToCamel(title.name);
        if (state.respiratory[camelName]) {
          state.respiratory[camelName].id = title.id;
          state.respiratory[camelName].classId = title.class_title_id;
        }
      });
    },

    updateFirstRunState(state, action) {
      state.componentState.firstRun = action.payload;
    },
    updateDataState(state, action) {
      state.componentState.dataState = action.payload;
    },
    updateFirstClick(state, action) {
      state.componentState.firstClick = action.payload;
    },
    updateVitalsDataState(state, action) {
      state.componentState.vitalsDataState = action.payload;
    },
  },
});

export const respiratoryActions = respiratorySlice.actions;

export default respiratorySlice.reducer;
